// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { signIn, signUp, logout } from "../auth"; // Named exports from auth.js
import { auth, db } from "../firebaseConfig"; // Import Firestore DB
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// Create Context
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null); // Stores user's role
  const [isAdmin, setIsAdmin] = useState(false); // True if role is "admin" or "superadmin"
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // True only if role is "superadmin"
  const [loading, setLoading] = useState(true); // Prevent pages from rendering before auth loads

  const fetchUserRole = async (user) => {
    if (!user) {
      setRole(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    try {
      // Fetch user role from Firestore
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userRole = userSnap.data().role;
        setRole(userRole);
        // Set isSuperAdmin only when role is "superadmin"
        setIsSuperAdmin(userRole === "superadmin");
        // Set isAdmin to true for both admin and superadmin
        setIsAdmin(userRole === "admin" || userRole === "superadmin");
      } else {
        // Default to non-admin if no role is found
        setRole(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
      setRole(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth State Changed -> User:", user);
  
      if (user) {
        try {
          // Force token refresh so that updated custom claims are loaded
          const idTokenResult = await user.getIdTokenResult(true);
          console.log("Token claims:", idTokenResult.claims);
  
          // Extract the role from the token claims.
          const userRole = idTokenResult.claims.role || null;
          setRole(userRole);
          setIsSuperAdmin(userRole === "superadmin");
          setIsAdmin(userRole === "admin" || userRole === "superadmin");
        } catch (error) {
          console.error("Error refreshing token:", error);
        }
      } else {
        setRole(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
      }
  
      setCurrentUser(user);
      setLoading(false);
    });
  
    return unsubscribe;
  }, []);
  
  
  
  const value = {
    currentUser,
    role,
    isAdmin,
    isSuperAdmin,
    signIn,
    signUp,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <p>Loading authentication...</p> : children} {/* Prevents rendering before loading */}
    </AuthContext.Provider>
  );
}

// Custom Hook
export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
